import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Pagination,
  Stack,
  TextField,
  CircularProgress,
} from "@mui/material";
import supabase from "../../shared/database/supabase";
import { toast, ToastContainer } from "react-toastify";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useListServiceOrders } from "../../features/service-order/list-service-order/api/useListIServiceOrder";
import ServiceOrdersListComponent from "../../features/service-order/list-service-order/ui/service-orders-list.component";
import _ from "lodash";

interface ListServiceOrdersPageProps {
  term: string;
  startDate: Date;
  endDate: Date;
  page: number;
  limit: number;
  shouldListByDate: boolean;
  enabled: boolean;
}

const YESTERDAY = new Date(new Date().getTime() - 24 * 60 * 60 * 1000);

const SearchServiceOrder = () => {
  const navigate = useNavigate();
  const [serviceOrderId, setServiceOrderId] = useState<string | null>(null);
  const user = supabase.auth.user();
  
  const [pageProps, setPageProps] = useState<ListServiceOrdersPageProps>({
    term: "",
    startDate: YESTERDAY,
    endDate: new Date(),
    page: 1,
    limit: 20,
    shouldListByDate: false,
    enabled: true
  });
  const [termMask, setTermMask] = useState<string>("");
  
  // Add isLoading and isFetching like cars page
  const { data, error, isLoading, isFetching } = useListServiceOrders(pageProps);
  const { page, limit } = pageProps;
  
  const handleGoToServiceOrder = () => {
    if (!serviceOrderId) {
      toast.error("Selecione uma ordem de serviço.");
      return;
    }
    navigate(`/service-orders/${serviceOrderId}/update`);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPageProps({ ...pageProps, page: value });
  };

  const debouncedSetPageProps = _.debounce(() => setPageProps({ ...pageProps, term: termMask, page: 1 }), 500);

  useEffect(() => {
    debouncedSetPageProps();
    return () => {
      debouncedSetPageProps.cancel();
    };
  }, [termMask]);

  // Only show full page loading on the very first load (when user hasn't typed anything yet)
  if (isLoading && !data && termMask === "") {
    return (
      <Container component="main" maxWidth="lg">
        <Box sx={{ 
          marginTop: 8,
          minHeight: "72vh",
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="lg">
      <Box sx={{ 
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        minHeight: "72vh"
      }}>
        {/* Title */}
        <Typography component="h1" variant="h4" sx={{ mb: 4 }}>
          Buscar Ordem de Serviço
        </Typography>
        
        {/* Search Box */}
        <Paper
          elevation={3}
          sx={{
            marginBottom: 4,
            padding: 4,
          }}
        >
          <TextField
            label="Buscar por cliente, placa ou modelo"
            variant="outlined"
            fullWidth
            value={termMask}
            onChange={(e) => setTermMask(e.target.value)}
          />
        </Paper>

        {/* Results Area - Flex grow to fill remaining space */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {isFetching ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              flex: 1,
              minHeight: '300px'
            }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Buscando ordens de serviço...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Service Orders List */}
              {data && data.service_orders && data.service_orders.length > 0 && (
                <>
                  <Box sx={{ flex: 1 }}>
                    <ServiceOrdersListComponent 
                      props={{ 
                        serviceOrders: data.service_orders 
                      }} 
                    />
                  </Box>
                  
                  <Stack spacing={2} sx={{ mt: 4, alignItems: 'center' }}>
                    <Pagination
                      count={Math.ceil(data.total / limit)}
                      size="large"
                      onChange={handlePageChange}
                      page={page}
                    />
                  </Stack>
                </>
              )}

              {/* No results */}
              {data && data.service_orders.length === 0 && termMask && (
                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                  minHeight: '300px'
                }}>
                  <Typography variant="h6" color="text.secondary">
                    Nenhuma ordem de serviço encontrada para "{termMask}"
                  </Typography>
                </Box>
              )}

              {/* Initial state */}
              {(!termMask || termMask === "") && (!data || data.service_orders.length === 0) && (
                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                  minHeight: '300px'
                }}>
                  <Typography variant="h6" color="text.secondary">
                    Digite um termo para buscar ordens de serviço
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
      <ToastContainer />
    </Container>
  );
};

export default SearchServiceOrder;